import React, { useState, useCallback, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody,
  FormControl, Button, Text, useDisclosure, RadioGroup, VStack, HStack,
  Box, Badge, Spinner, Alert, AlertIcon, useToast, Heading,
  Flex, Icon, Center, Stack, Container, useColorModeValue, Progress
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { loadSaveSlots, getUserEmail } from "../../services/userSettingsService";
import { fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import { 
  FiCheck, FiClock, FiArrowRight, FiShield, 
  FiCheckCircle, FiAlertCircle
} from "react-icons/fi";

// Motion components
const MotionBox = motion(Box);

// Constants
const API_ENDPOINT = "https://q0tw9j2tqb.execute-api.eu-north-1.amazonaws.com/prod/create-checkout";
const REQUEST_TIMEOUT = 15000;

// Helper function for formatting dates
const formatDateRelative = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 5) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

export const useRequestFormModal = (settings, accentColor = "blue.500") => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isProcessing, setIsProcessing] = useState(false);
  const [slotData, setSlotData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [step, setStep] = useState(1);
  const toast = useToast();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  
  // Theme colors
  const accentColorBase = accentColor ? accentColor.split('.')[0] : "blue";
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const highlightBg = useColorModeValue(`${accentColorBase}.50`, `${accentColorBase}.900`);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setApiError(null);
      setApiResponse(null);
      setHasSubscription(false);
      setCheckingSubscription(false);
      setEmailError('');
      setIsProcessing(false);
      
      const loadData = async () => {
        setIsLoading(true);
        
        try {
          // Fetch user email
          const userEmailResult = await getUserEmail();
          if (userEmailResult.success) {
            setUserEmail(userEmailResult.userEmail);
          } else {
            setEmailError('Could not retrieve your email address. Please contact support.');
          }
          
          // Fetch saved slots
          const result = await loadSaveSlots();
          if (result.success) {
            setSlotData(result.slotData);
            
            // Select active slot or first available slot
            const activeSlot = result.slotData.save_slots.find(slot => slot.active);
            if (activeSlot) {
              setSelectedSlot(activeSlot.slot);
            } else if (result.slotData.save_slots.some(slot => slot.settings)) {
              const firstWithSettings = result.slotData.save_slots.find(slot => slot.settings);
              if (firstWithSettings) {
                setSelectedSlot(firstWithSettings.slot);
              }
            }
          }
        } catch (err) {
          toast({
            title: "Error loading data",
            description: "Failed to load your information.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsLoading(false);
        }
      };
      
      loadData();
    }
  }, [isOpen, toast]);

  // API request function with authentication
  const makeApiRequest = async (data) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      // Get auth token
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      
      if (!token) throw new Error("Authentication failed");
      
      // Make API request
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(data),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Parse response
      const responseText = await response.text();
      let parsedResponse;
      
      try {
        parsedResponse = JSON.parse(responseText);
        if (parsedResponse.body && typeof parsedResponse.body === 'string') {
          try {
            const bodyJson = JSON.parse(parsedResponse.body);
            parsedResponse = bodyJson;
          } catch (e) {}
        }
      } catch (e) {
        throw new Error(`Invalid response: ${responseText.substring(0, 100)}...`);
      }
      
      if (!response.ok || !parsedResponse.success) {
        throw { 
          message: parsedResponse?.error || `Server error: ${response.status}`,
          code: parsedResponse?.code || 'UNKNOWN_ERROR'
        };
      }
      
      return parsedResponse;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw { message: "Request timed out", code: "TIMEOUT_ERROR" };
      }
      throw error;
    }
  };

  // Check subscription status
  const checkSubscriptionStatus = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedSlot) {
      toast({
        title: "No configuration selected",
        description: "Please select a widget configuration to continue.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setCheckingSubscription(true);
    setApiError(null);
    
    try {
      const user = await getCurrentUser();
      const result = await makeApiRequest({
        userId: user.userId,
        widgetId: selectedSlot,
        userEmail,
        checkSubscriptionOnly: true
      });
      
      setApiResponse(result);
      setHasSubscription(!!result.existingSubscription);
      setIsFirstTimeUser(!result.existingUser);
      setStep(2);
    } catch (error) {
      // If check fails, assume no subscription & proceed to payment
      console.error("Subscription check failed:", error);
      setHasSubscription(false);
      setIsFirstTimeUser(true);
      setStep(2);
    } finally {
      setCheckingSubscription(false);
    }
  }, [selectedSlot, userEmail, toast, makeApiRequest]);

  // Handle widget creation or checkout redirection
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    setIsProcessing(true);
    setApiError(null);
    
    try {
      const user = await getCurrentUser();
      
      // If user has subscription from prior check, use that response
      if (apiResponse?.existingSubscription) {
        toast({
          title: "Widget created successfully",
          description: "Your chatbot has been created and will be available shortly.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        
        const redirectUrl = apiResponse.widgetData?.widgetId 
          ? `/widget-success?widgetId=${apiResponse.widgetData.widgetId}` 
          : "/widget-success";
        
        window.location.href = redirectUrl;
        return;
      }
      
      // Otherwise make API call to create widget/subscription
      const result = await makeApiRequest({
        userId: user.userId,
        widgetId: selectedSlot,
        userEmail,
        createSubscription: !hasSubscription
      });
      
      // Handle response based on result
      if (result.existingSubscription) {
        // Widget created successfully with existing subscription
        toast({
          title: "Widget created successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        
        const redirectUrl = result.widgetData?.widgetId 
          ? `/widget-success?widgetId=${result.widgetData.widgetId}` 
          : "/widget-success";
        
        window.location.href = redirectUrl;
      } 
      else if (result.requiresPayment && result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      }
      else {
        toast({
          title: "Process completed",
          description: "Please check your account dashboard for status updates.",
          status: "info",
          duration: 9000,
          isClosable: true,
        });
        
        window.location.href = "/dashboard";
      }
    } catch (error) {
      console.error("Processing error:", error);
      
      // Try forced checkout creation for subscription errors
      if (error.message?.includes("subscription") || error.message?.includes("verify") || 
          error.code === 'SUBSCRIPTION_ERROR' || error.code === 'VERIFICATION_ERROR') {
        
        try {
          const user = await getCurrentUser();
          const checkoutResult = await makeApiRequest({
            userId: user.userId,
            widgetId: selectedSlot,
            userEmail,
            forceCreateCheckout: true,
            skipSubscriptionVerification: true
          });
          
          if (checkoutResult.checkoutUrl) {
            window.location.href = checkoutResult.checkoutUrl;
            return;
          }
        } catch (checkoutError) {
          console.error("Forced checkout creation failed:", checkoutError);
        }
      }
      
      // Show error message
      setApiError(error);
      
      // User-friendly error message
      let errorTitle = "Widget creation failed";
      let errorMessage = error.message || "We're experiencing technical difficulties";
      
      if (error.code === 'SERVICE_UNAVAILABLE') {
        errorTitle = "Widget creation temporarily unavailable";
        errorMessage = "Creating new widgets is currently deactivated. Please try again later.";
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      
    } finally {
      setIsProcessing(false);
    }
  }, [userEmail, selectedSlot, apiResponse, hasSubscription, toast, makeApiRequest]);

  // Go back to configuration selection
  const goToPrevStep = () => {
    setStep(1);
    setApiError(null);
  };

  // Modal component
  const RequestFormModalComponent = useCallback(() => (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={{ base: "sm", md: "md" }}
      motionPreset="slideInBottom"
      isCentered
    >
      <ModalOverlay backdropFilter="blur(4px)" bg="rgba(0, 0, 0, 0.3)" />
      <ModalContent borderRadius="2xl" overflow="hidden" boxShadow="xl" mx={{ base: 4, md: 0 }} bg={bgColor}>
        <ModalCloseButton size="lg" color={textColor} top={4} right={4} zIndex={2} />
        <ModalBody p={0}>
          <Box py={8} px={6}>
            {/* Header */}
            <Heading size="md" fontWeight="semibold" textAlign="center" mb={2}>
              {step === 1 
                ? "Select Configuration" 
                : (hasSubscription ? "Create Your Chatbot" : "Subscribe to Chatbot")}
            </Heading>
            
            {/* Step indicator */}
            <Flex justify="center" mb={6}>
              <HStack spacing={2}>
                <Flex 
                  h="8px" w="8px" borderRadius="full" 
                  bg={step === 1 ? `${accentColorBase}.500` : "gray.200"}
                  onClick={() => step !== 1 && goToPrevStep()}
                  cursor={step !== 1 ? "pointer" : "default"}
                />
                <Flex 
                  h="8px" w="8px" borderRadius="full" 
                  bg={step === 2 ? `${accentColorBase}.500` : "gray.200"} 
                />
              </HStack>
            </Flex>
            
            {/* Show API errors */}
            {apiError && (
              <Alert 
                status="error" variant="subtle" borderRadius="xl" mb={4}
                flexDirection="column" alignItems="center" justifyContent="center"
                textAlign="center" py={4}
              >
                <Icon as={FiAlertCircle} boxSize={6} mr={0} mb={2} />
                <AlertIcon display="none" />
                <Text fontWeight="bold" fontSize="md">
                  {apiError.code === 'SERVICE_UNAVAILABLE' 
                    ? "Widget Creation Temporarily Unavailable" 
                    : "Widget Creation Failed"}
                </Text>
                <Text mt={2}>
                  {apiError.code === 'SERVICE_UNAVAILABLE'
                    ? "Creating new widgets is currently deactivated. Please try again later."
                    : apiError.message || "There was an error processing your request."}
                </Text>
              </Alert>
            )}
            
            {/* Email info on step 1 */}
            {step === 1 && (
              isLoading ? (
                <Center py={4}>
                  <Spinner size="sm" color={accentColor} mr={2} />
                  <Text fontSize="sm">Loading your information...</Text>
                </Center>
              ) : emailError ? (
                <Alert status="error" borderRadius="xl" mb={4}>
                  <AlertIcon />
                  <Text fontSize="sm">{emailError}</Text>
                </Alert>
              ) : (
                <MotionBox
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  p={4} borderRadius="xl" bg={highlightBg} mb={6}
                >
                  <Flex align="center">
                    <Icon as={FiShield} boxSize={5} mr={3} color={`${accentColorBase}.500`} />
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Your chatbot will be sent to</Text>
                      <Text fontWeight="bold">{userEmail}</Text>
                    </Box>
                  </Flex>
                </MotionBox>
              )
            )}

            {/* Subscription check loading indicator */}
            {checkingSubscription && (
              <Box mb={6}>
                <Text fontSize="sm" textAlign="center" mb={3}>
                  Checking your account status...
                </Text>
                <Progress size="xs" isIndeterminate colorScheme={accentColorBase} borderRadius="xl" />
              </Box>
            )}

            {/* Step 1: Configuration selection */}
            {step === 1 && (
              <Box>
                <FormControl>
                  <Text fontWeight="medium" mb={4}>Choose a widget configuration:</Text>
                  
                  {isLoading ? (
                    <VStack spacing={4} align="stretch">
                      {[1, 2].map(i => (
                        <Box 
                          key={i} p={4} borderRadius="xl" borderWidth="1px"
                          borderColor={borderColor} bg={bgColor} boxShadow="sm"
                        >
                          <Flex justify="space-between" align="center" mb={2}>
                            <Box height="20px" width="140px" bg="gray.100" borderRadius="md" />
                            <Box height="20px" width="50px" bg="gray.100" borderRadius="md" />
                          </Flex>
                          <Box height="16px" width="70%" bg="gray.100" borderRadius="md" mt={2} />
                        </Box>
                      ))}
                    </VStack>
                  ) : slotData?.save_slots?.some(slot => slot.settings) ? (
                    <RadioGroup onChange={setSelectedSlot} value={selectedSlot}>
                      <VStack align="stretch" spacing={4}>
                        {slotData.save_slots
                          .filter(slot => slot.settings)
                          .map((slot) => (
                            <MotionBox
                              key={slot.slot}
                              whileHover={{ y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              transition={{ duration: 0.2 }}
                              onClick={() => setSelectedSlot(slot.slot)}
                              cursor="pointer"
                            >
                              <Box 
                                p={4} borderWidth="2px" borderRadius="xl"
                                borderColor={selectedSlot === slot.slot ? `${accentColorBase}.500` : borderColor}
                                bg={selectedSlot === slot.slot ? highlightBg : bgColor}
                                boxShadow={selectedSlot === slot.slot ? "md" : "sm"}
                              >
                                <Flex justify="space-between" align="center">
                                  <Box>
                                    <Flex align="center">
                                      <Text 
                                        fontWeight="semibold" fontSize="md"
                                        color={selectedSlot === slot.slot ? `${accentColorBase}.700` : textColor}
                                      >
                                        {slot.name || `Configuration ${slot.slot}`}
                                      </Text>
                                      {slot.active && (
                                        <Badge 
                                          ml={2} colorScheme="green" variant="subtle"
                                          fontSize="xs" borderRadius="full" px={2}
                                        >
                                          Active
                                        </Badge>
                                      )}
                                    </Flex>
                                    
                                    {slot.updatedAt && (
                                      <Flex align="center" mt={1}>
                                        <Icon as={FiClock} size="xs" color={mutedTextColor} mr={1} />
                                        <Text fontSize="xs" color={mutedTextColor}>
                                          Updated {formatDateRelative(slot.updatedAt)}
                                        </Text>
                                      </Flex>
                                    )}
                                  </Box>
                                  
                                  {selectedSlot === slot.slot && (
                                    <Flex 
                                      w="24px" h="24px" bg={`${accentColorBase}.500`}
                                      color="white" borderRadius="full"
                                      align="center" justify="center"
                                    >
                                      <Icon as={FiCheck} />
                                    </Flex>
                                  )}
                                </Flex>
                              </Box>
                            </MotionBox>
                          ))}
                      </VStack>
                    </RadioGroup>
                  ) : (
                    <Alert status="warning" variant="subtle" borderRadius="xl">
                      <AlertIcon />
                      <Box fontSize="sm">
                        <Text fontWeight="medium">No saved configurations</Text>
                        <Text>Please save a configuration before continuing.</Text>
                      </Box>
                    </Alert>
                  )}
                </FormControl>
              </Box>
            )}

            {/* Step 2: Subscription details */}
            {step === 2 && !apiError && (
              <Container maxW="container.sm" p={0}>
                <MotionBox
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  borderRadius="xl" borderWidth="1px"
                  borderColor={borderColor} overflow="hidden"
                >
                  <Box bg={bgColor} p={5}>
                    <Heading size="sm" fontWeight="semibold" mb={4}>
                      {hasSubscription ? "Ready to Create" : "Subscription Details"}
                    </Heading>
                    
                    {hasSubscription ? (
                      <Stack spacing={4}>
                        <Flex p={4} bg={highlightBg} borderRadius="lg" align="flex-start">
                          <Icon as={FiCheckCircle} color={`${accentColorBase}.500`} boxSize={5} mt={0.5} mr={3} />
                          <Box>
                            <Text fontWeight="bold">Active Subscription Detected</Text>
                            <Text fontSize="sm" mt={1}>
                              Your widget will be created immediately without additional charges.
                            </Text>
                          </Box>
                        </Flex>
                      </Stack>
                    ) : (
                      <Stack spacing={4}>
                        <Flex justify="space-between" align="center">
                          <Text>Monthly subscription</Text>
                          <Text fontWeight="bold">$59.99/month</Text>
                        </Flex>
                        
                        {isFirstTimeUser && (
                          <MotionBox
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                            mt={2}
                          >
                            <Flex p={4} bg={highlightBg} borderRadius="lg" align="flex-start">
                              <Icon as={FiCheckCircle} color={`${accentColorBase}.500`} boxSize={5} mt={0.5} mr={3} />
                              <Box>
                                <Text fontWeight="bold">It looks like your 30-Day free trial has expired</Text>
                                <Text fontSize="sm" mt={1}>
                                  Get your chatbot up and running with a monthly subscription.
                                </Text>
                              </Box>
                            </Flex>
                          </MotionBox>
                        )}
                        
                        <Box pt={2}>
                          <Text fontSize="sm" color={mutedTextColor}>
                            You'll be redirected to our secure payment processor to complete your subscription.
                          </Text>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </MotionBox>
              </Container>
            )}
            
            {/* Action Buttons */}
            <Flex mt={8} justify={step === 1 ? "flex-end" : "space-between"}>
              {step === 2 && (
                <Button
                  variant="ghost" onClick={goToPrevStep}
                  fontSize="sm" fontWeight="medium"
                >
                  Back
                </Button>
              )}
              
              <Button
                colorScheme={accentColorBase}
                onClick={step === 1 ? checkSubscriptionStatus : handleSubmit}
                isLoading={step === 1 ? checkingSubscription : isProcessing}
                loadingText={step === 1 ? "Checking..." : (hasSubscription ? "Creating..." : "Processing...")}
                isDisabled={
                  !selectedSlot || isLoading || !!emailError || 
                  !slotData?.save_slots?.some(slot => slot.settings) ||
                  (apiError && apiError.code === 'SERVICE_UNAVAILABLE')
                }
                rightIcon={step === 1 ? <FiArrowRight /> : undefined}
                boxShadow="md" borderRadius="xl" px={6}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                _active={{ transform: "translateY(0)" }}
              >
                {step === 1 
                  ? "Continue" 
                  : (hasSubscription ? "Create Chatbot" : "Subscribe Now")}
              </Button>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  ), [
    isOpen, onClose, step, handleSubmit, userEmail, emailError, 
    isProcessing, selectedSlot, slotData, isLoading, 
    accentColor, accentColorBase, hasSubscription, 
    isFirstTimeUser, highlightBg, bgColor,
    textColor, mutedTextColor, borderColor, apiError, 
    checkSubscriptionStatus, checkingSubscription
  ]);

  return {
    isOpen,
    onOpen,
    onClose,
    RequestFormModalComponent
  };
};